import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MobileBottomBar } from "@/components/MobileBottomBar";
import { FinalCtaSection } from "@/components/FinalCtaSection";
import { EliteClientRoster } from "@/components/EliteClientRoster";

const GeoUK = () => {
  useEffect(() => {
    document.title = "RevOps Specialist UK | Marjohn Robillo";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Hire an elite RevOps Specialist from the Philippines for your UK-based business. Strategic timezone overlap and high-end technical automation.",
      );
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      "content",
      "RevOps specialist UK, revenue operations UK, CRM automation UK, GoHighLevel expert UK, n8n developer UK, technical VA UK, Philippines automation expert, offshore RevOps UK",
    );

    const ogTags: Record<string, string> = {
      "og:title": "RevOps Specialist UK | Marjohn Robillo",
      "og:description":
        "Hire an elite RevOps Specialist from the Philippines for your UK-based business. Strategic timezone overlap and high-end technical automation.",
      "og:type": "website",
      "og:url": "https://marjohnrobillo.me/revops-specialist-uk",
      "og:image":
        "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
      "twitter:card": "summary_large_image",
      "twitter:title": "RevOps Specialist UK | Marjohn Robillo",
      "twitter:description":
        "Hire an elite RevOps Specialist for your UK-based business.",
      "twitter:image":
        "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
    };
    Object.entries(ogTags).forEach(([name, content]) => {
      let element =
        document.querySelector(`meta[property="${name}"]`) ||
        document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(
          name.startsWith("og:") ? "property" : "name",
          name,
        );
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    });

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute(
      "href",
      "https://marjohnrobillo.me/revops-specialist-uk",
    );
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "RevOps Specialist for UK Agencies",
    description:
      "Hire an elite RevOps Specialist from the Philippines for your UK-based business. Strategic timezone overlap and high-end technical automation.",
    url: "https://marjohnrobillo.me/revops-specialist-uk",
    areaServed: ["GB"],
    address: { "@type": "PostalAddress", addressCountry: "PH" },
    priceRange: "$$",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Can a Philippines-based RevOps specialist work UK business hours?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Structured working hours overlap with the UK morning and afternoon, ensuring smooth handoffs and daily alignment.",
        },
      },
      {
        "@type": "Question",
        name: "How much does a RevOps specialist cost compared to a UK hire?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A senior RevOps specialist from the Philippines typically costs $1,500-$4,000/month compared to $8,000-$15,000 for equivalent UK-based talent, leveraging the strength of the Pound.",
        },
      },
    ],
  };

  const features = [
    {
      title: "Strategic Overlap",
      desc: "Structured working hours to overlap with the UK morning/afternoon, ensuring smooth handoffs and daily alignment.",
    },
    {
      title: "Elite RevOps",
      desc: "Moving beyond basic tasks to architect full lead-to-cash pipelines, CRM data hygiene, and automated reporting.",
    },
    {
      title: "GBP Arbitrage",
      desc: "Leverage the strength of the Pound to secure top-tier technical talent in the Philippines that would cost 4x locally.",
    },
  ];

  return (
    <main
      className="min-h-screen relative pb-[72px] md:pb-0"
      style={{ background: "#FFFFFF" }}
    >
      <MobileBottomBar />
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-24 px-6 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 72% 30%, rgba(11,124,242,0.10) 0%, transparent 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-6 right-0 select-none hidden md:block"
        >
          <span
            className="font-display font-black leading-none"
            style={{
              fontSize: "clamp(160px, 22vw, 320px)",
              color: "rgba(17,143,247,0.07)",
              letterSpacing: "-0.07em",
            }}
          >
            UK
          </span>
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 mb-8 justify-center">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: "#27DC5D" }}
            />
            <span
              className="font-sans text-xs font-semibold uppercase tracking-[0.22em]"
              style={{ color: "var(--primary)" }}
            >
              🇬🇧 Helping UK-Based SaaS &amp; Businesses
            </span>
          </div>

          <h1
            className="font-display font-extrabold mb-7 leading-[0.95]"
            style={{
              fontSize: "clamp(40px, 5.6vw, 76px)",
              letterSpacing: "-0.045em",
              color: "#071E61",
            }}
          >
            Hire a RevOps Specialist &amp; CRM Automation Expert from the
            Philippines for{" "}
            <span style={{ color: "var(--primary)" }}>UK Agencies</span>
          </h1>

          <p
            className="mx-auto mb-10"
            style={{
              fontSize: "clamp(17px, 1.5vw, 20px)",
              lineHeight: 1.55,
              color: "#536B89",
              maxWidth: "640px",
            }}
          >
            Scale your UK business with world-class Revenue Operations and
            technical automation. Get enterprise-grade systems at an offshore
            arbitrage advantage.
          </p>
        </div>
      </section>

      <EliteClientRoster />

      {/* Feature cards */}
      <section className="relative py-20 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="relative rounded-[24px] p-8 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
                border: "1px solid rgba(8,46,103,0.07)",
                boxShadow: "0 14px 40px rgba(8,46,103,0.08)",
              }}
            >
              <div
                className="font-display font-black mb-5 select-none"
                style={{
                  fontSize: "56px",
                  lineHeight: 1,
                  color: "rgba(17,143,247,0.08)",
                  letterSpacing: "-0.04em",
                }}
              >
                0{i + 1}
              </div>
              <h3
                className="font-display font-bold mb-3"
                style={{
                  fontSize: "22px",
                  color: "#071E61",
                  letterSpacing: "-0.03em",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{ fontSize: "15px", lineHeight: 1.55, color: "#536B89" }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <FinalCtaSection />
      <Footer />
    </main>
  );
};

export default GeoUK;
